/**
 * PATH: packages/ui/src/components/mobile-dock/useMobileDockChromeState.ts
 * FILE_ID: MASTER-MOBILEDOCK-CHROME-STATE-001
 * PURPOSE: MobileDock collapsed state + the published floor-reserve CSS vars.
 * LANDMINE: a page asking for the floor (useRequestMobileDockCollapse) collapses
 *   the dock WITHOUT touching the stored preference; leaving that page returns
 *   to it. The shell never sniffs URLs, it only reads the <html> signals.
 * RELATED: index.tsx, @quotech/ui/mobile mobileDockSignals.ts.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  MOBILE_DOCK_AUTO_COLLAPSE_ATTR,
  MOBILE_DOCK_COLLAPSED_ATTR,
  MOBILE_DOCK_RESTORE_EVENT,
  MOBILE_DOCK_RESTORE_HOST_ATTR,
  useRootAttr,
} from '@quotech/ui/mobile'

/** Published CSS var so the canvas footer + UniversalDock can lift
 *  above the strip instead of being overlaid. Compacted from 80px so the
 *  dock consumes less vertical space on tablet/phone while its tiles keep a
 *  ≥44px (accessible) touch target. */
const MOBILE_DOCK_HEIGHT_PX = 64
const MOBILE_DOCK_HEIGHT_VAR = '--mobile-dock-h'
/** Collapsed "Menu" restore pill: ~36px control + 8px floor margin = its
 *  TRUE footprint. Kept as a named constant so --mobile-dock-h can still
 *  reserve floor space when the full dock is hidden — otherwise sticky
 *  composers (Diary hours input) sit under the pill
 *  (BUG-2026-07-17-MOBILE-MENU-COMPOSER-OVERLAP). Was 48, trimmed to the
 *  real footprint: the extra air read as a dead band under the Diary
 *  composer (operator 2026-08-31). Only reserved while the pill is at a
 *  BOTTOM corner — see restoreAtBottom below. */
const MOBILE_DOCK_RESTORE_HEIGHT_PX = 44
/** BUG-2026-07-25-KANBAN-COLLAPSED-DOCK-DEAD-BAND — height of the FULL-WIDTH
 *  strip only, which is 0 while collapsed because the restore pill is a
 *  bottom-LEFT corner control (`fixed left-2 bottom-0`), not a bar.
 *
 *  `--mobile-dock-h` deliberately conflates the two: sticky composers and
 *  corner FABs must clear the pill, so it stays non-zero when collapsed. But a
 *  consumer that spends the value as a full-width in-flow gutter (the kanban
 *  board root) then reserves 48px across the whole viewport for an 85px corner
 *  pill — a dead band under the board with the horizontal scrollbar floating
 *  above the viewport floor (operator screenshot 2026-07-25, build 715b857b).
 *
 *  Those consumers read this var instead and fall back to `--mobile-dock-h`,
 *  so hosts that never publish it (tenant shells, standalone hub SPAs) keep
 *  their current behaviour exactly. */
const MOBILE_DOCK_STRIP_HEIGHT_VAR = '--mobile-dock-strip-h'

/** Persist the collapsed preference so hiding the dock on one page keeps it
 *  hidden across navigation until the operator taps the floating "Menu"
 *  button to restore it. Open/closed is preference-only — never auto-dismiss
 *  on outside tap or route change (operator 2026-07-19: keep menu open until
 *  someone collapses it). */
const MOBILE_DOCK_COLLAPSED_KEY = 'quotech.master-mobile-dock.collapsed'

export interface MobileDockChromeState {
  collapsed: boolean
  collapse: () => void
  restore: () => void
  /** A page asked for the floor: the restore control shrinks to a 4-dot button. */
  compactRestore: boolean
  /** A page shows the restore control in its own chrome; the floating one hides. */
  restoreHosted: boolean
}

export function useMobileDockChromeState({
  shouldShow,
  dockAtTop,
  restoreAtBottom,
}: {
  shouldShow: boolean
  dockAtTop: boolean
  restoreAtBottom: boolean
}): MobileDockChromeState {
  // Collapse-to-floating-button: hide the whole dock (slide down) leaving only
  // a small "Menu →" pill that restores it. Preference persists across pages.
  // Open stays open until the operator taps the collapse control — tapping the
  // page (e.g. entering Diary chat) must NOT auto-dismiss the menu.
  const [storedCollapsed, setStoredCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true
    try {
      return window.localStorage.getItem(MOBILE_DOCK_COLLAPSED_KEY) !== '0'
    } catch {
      return true
    }
  })
  const setCollapsedPersisted = useCallback((next: boolean) => {
    setStoredCollapsed(next)
    try {
      window.localStorage.setItem(MOBILE_DOCK_COLLAPSED_KEY, next ? '1' : '0')
    } catch {
      /* storage unavailable (private mode / SSR) — in-memory state still works */
    }
  }, [])

  // The attr value is a claim generation token: a new token means a new page
  // mount asked for the floor, even if the old claim ended in the same batch.
  const hostToken = useRootAttr(MOBILE_DOCK_AUTO_COLLAPSE_ATTR)
  const hostCollapse = hostToken !== null
  const restoreHost = useRootAttr(MOBILE_DOCK_RESTORE_HOST_ATTR) !== null
  // Session-only choice while a page holds the floor; null = not chosen yet.
  const [hostOverride, setHostOverride] = useState<boolean | null>(null)
  useEffect(() => {
    setHostOverride(null)
  }, [hostToken])
  const collapsed = hostCollapse ? (hostOverride ?? true) : storedCollapsed

  const collapse = useCallback(() => {
    if (hostCollapse) setHostOverride(true)
    else setCollapsedPersisted(true)
  }, [hostCollapse, setCollapsedPersisted])
  const restore = useCallback(() => {
    if (hostCollapse) setHostOverride(false)
    else setCollapsedPersisted(false)
  }, [hostCollapse, setCollapsedPersisted])
  useEffect(() => {
    window.addEventListener(MOBILE_DOCK_RESTORE_EVENT, restore)
    return () => window.removeEventListener(MOBILE_DOCK_RESTORE_EVENT, restore)
  }, [restore])

  const restoreHosted = shouldShow && collapsed && restoreHost

  // Publish + clear the CSS var. We deliberately set 0px (not unset)
  // when wide so `calc(var(--mobile-dock-h, 0px) + …)` resolves the
  // same on both sides of the breakpoint — no flicker.
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (shouldShow && !collapsed && !dockAtTop) {
      // Include the bottom safe-area inset (home indicator / gesture bar) so
      // every clearance consumer of --mobile-dock-h (page gutters, kanban
      // footer, toasts) reserves the dock's TRUE occupied height on notched
      // devices. env() resolves to 0 unless index.html sets viewport-fit=cover.
      document.documentElement.style.setProperty(
        MOBILE_DOCK_HEIGHT_VAR,
        `calc(${MOBILE_DOCK_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`,
      )
      // Expanded: the strip IS full-width, so both vars agree.
      document.documentElement.style.setProperty(
        MOBILE_DOCK_STRIP_HEIGHT_VAR,
        `calc(${MOBILE_DOCK_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`,
      )
    } else if (shouldShow && collapsed) {
      if (restoreHosted) {
        // The page's own in-flow chrome carries the restore control, so only
        // the home-indicator inset sits under it.
        document.documentElement.style.setProperty(MOBILE_DOCK_HEIGHT_VAR, 'env(safe-area-inset-bottom, 0px)')
      } else if (restoreAtBottom) {
        // BUG-2026-07-17-MOBILE-MENU-COMPOSER-OVERLAP: the floating Menu pill
        // still occupies a bottom corner. Keep a short --mobile-dock-h so
        // UnifiedLayout / diary / kanban gutters lift sticky composers above
        // it instead of reclaiming the floor to zero.
        document.documentElement.style.setProperty(
          MOBILE_DOCK_HEIGHT_VAR,
          `calc(${MOBILE_DOCK_RESTORE_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`,
        )
      } else {
        // Pill dragged to a TOP corner: nothing occupies the floor, so pages
        // must not keep a dead band under their composers (mirrors how the
        // EXPANDED dock already clears the reserve when snapped to the top).
        document.documentElement.style.setProperty(MOBILE_DOCK_HEIGHT_VAR, '0px')
      }
      // Collapsed: nothing spans the width, so full-width gutters reserve 0.
      document.documentElement.style.setProperty(MOBILE_DOCK_STRIP_HEIGHT_VAR, '0px')
    } else {
      // Wide desktop: no dock chrome — clear the vars entirely.
      document.documentElement.style.removeProperty(MOBILE_DOCK_HEIGHT_VAR)
      document.documentElement.style.removeProperty(MOBILE_DOCK_STRIP_HEIGHT_VAR)
    }
    if (shouldShow) {
      document.documentElement.setAttribute(MOBILE_DOCK_COLLAPSED_ATTR, collapsed ? 'true' : 'false')
    } else {
      document.documentElement.removeAttribute(MOBILE_DOCK_COLLAPSED_ATTR)
    }
  }, [shouldShow, collapsed, dockAtTop, restoreAtBottom, restoreHosted])
  useEffect(() => () => document.documentElement.removeAttribute(MOBILE_DOCK_COLLAPSED_ATTR), [])

  return { collapsed, collapse, restore, compactRestore: hostCollapse, restoreHosted }
}
